import React from "react";
import config from "../config";

export default function Terms() {
  const { company } = config;

  return (
    <div className="pt-[72px]">
      {/* Header */}
      <section className="border-b border-white/5 bg-slate-950 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-extrabold text-white">
            Terms of Service
          </h1>
          <p className="mt-3 text-slate-400">
            Please read these terms carefully before using our services.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <p className="mb-10 text-sm text-slate-500">
            Last updated: January 2025
          </p>

          <div className="space-y-10 text-slate-300">
            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                1. Acceptance of Terms
              </h2>
              <p className="leading-relaxed">
                By accessing or using the services provided by {company.name} (&ldquo;
                {company.domain}&rdquo;), you agree to be bound by these Terms of
                Service. If you do not agree to these terms, please do not use
                our services. These terms apply to all visitors, users, and
                clients who access or use our services.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                2. Services
              </h2>
              <p className="leading-relaxed">
                {company.name} provides software development services including
                but not limited to iOS app development, game development, Steam
                publishing, workflow automation, AI integration, web
                development, REST API development, Unity3D plugin development,
                and technical consultancy.
              </p>
              <p className="mt-3 leading-relaxed">
                All services are provided on a project basis or hourly basis as
                agreed upon between {company.name} and the client. Service
                scope, deliverables, timelines, and pricing are defined in
                individual project agreements or proposals.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                3. Intellectual Property
              </h2>
              <h3 className="mb-2 font-semibold text-slate-200">
                3.1 Client Ownership
              </h3>
              <p className="leading-relaxed">
                Upon full payment, the client receives ownership of all custom
                work product created specifically for their project, unless
                otherwise specified in the project agreement.
              </p>
              <h3 className="mb-2 mt-4 font-semibold text-slate-200">
                3.2 Pre-existing Materials
              </h3>
              <p className="leading-relaxed">
                {company.name} retains ownership of all pre-existing tools,
                libraries, frameworks, and code that existed prior to the
                project or are developed independently. The client receives a
                non-exclusive license to use these materials as part of their
                delivered project.
              </p>
              <h3 className="mb-2 mt-4 font-semibold text-slate-200">
                3.3 Portfolio Rights
              </h3>
              <p className="leading-relaxed">
                {company.name} reserves the right to display completed work in
                its portfolio and marketing materials unless a non-disclosure
                agreement is in place.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                4. Payment Terms
              </h2>
              <p className="leading-relaxed">
                Payment terms are outlined in individual project agreements.
                General terms include:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>
                  A deposit may be required before work begins, typically
                  25&ndash;50% of the project total.
                </li>
                <li>
                  Milestone-based payments may apply for larger projects.
                </li>
                <li>
                  Hourly consultancy is billed at the agreed rate with a
                  minimum of 1 hour.
                </li>
                <li>
                  Invoices are due upon receipt unless otherwise agreed in
                  writing.
                </li>
                <li>
                  Late payments may incur additional fees or result in
                  suspension of services.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                5. Confidentiality
              </h2>
              <p className="leading-relaxed">
                Both parties agree to maintain the confidentiality of any
                proprietary or sensitive information shared during the course of
                a project. This includes but is not limited to business plans,
                technical specifications, source code, and financial
                information.
              </p>
              <p className="mt-3 leading-relaxed">
                Confidentiality obligations survive the termination of any
                project agreement and remain in effect indefinitely unless the
                information becomes publicly available through no fault of the
                receiving party.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                6. Limitation of Liability
              </h2>
              <p className="leading-relaxed">
                To the maximum extent permitted by applicable law, {company.name}{" "}
                shall not be liable for any indirect, incidental, special,
                consequential, or punitive damages, including but not limited to
                loss of profits, data, or business opportunities, arising out of
                or in connection with our services.
              </p>
              <p className="mt-3 leading-relaxed">
                Our total liability for any claim arising from or related to our
                services shall not exceed the total amount paid by the client for
                the specific project giving rise to the claim.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                7. Termination
              </h2>
              <p className="leading-relaxed">
                Either party may terminate a project agreement with written
                notice. In the event of termination:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>
                  The client is responsible for payment of all work completed
                  up to the date of termination.
                </li>
                <li>
                  Any deposits paid for incomplete milestones may be partially
                  refunded at {company.name}&rsquo;s discretion.
                </li>
                <li>
                  All completed deliverables paid for will be transferred to
                  the client.
                </li>
                <li>
                  {company.name} reserves the right to terminate services if
                  the client fails to meet payment obligations or engages in
                  abusive conduct.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                8. Governing Law
              </h2>
              <p className="leading-relaxed">
                These Terms of Service shall be governed by and construed in
                accordance with applicable laws. Any disputes arising from these
                terms or our services shall be resolved through good-faith
                negotiation. If a resolution cannot be reached, disputes shall be
                submitted to binding arbitration.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                9. Contact Information
              </h2>
              <p className="leading-relaxed">
                If you have any questions about these Terms of Service, please
                contact us at{" "}
                <a
                  href={`mailto:${company.email}`}
                  className="text-cyan-400 hover:text-cyan-300"
                >
                  {company.email}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
